import type { CharacterSprites } from "../sprites/data.js";
import type { Character, SpriteData, LayoutData } from "../types/index.js";
import {
  WALK_SPEED_PX_PER_SEC,
  WALK_FRAME_DURATION_SEC,
  TYPE_FRAME_DURATION_SEC,
  MATRIX_EFFECT_DURATION_SEC,
  WAITING_BUBBLE_DURATION_SEC,
  MESSAGE_BUBBLE_DURATION_SEC,
  IDLE_BEFORE_WANDER_SEC,
  IDLE_COUCH_MIN_SEC,
  IDLE_COUCH_MAX_SEC,
  IDLE_COFFEE_MIN_SEC,
  IDLE_COFFEE_MAX_SEC,
  IDLE_STAND_MIN_SEC,
  IDLE_STAND_MAX_SEC,
  IDLE_POINTS,
  MEETING_DURATION_MIN_SEC,
  MEETING_DURATION_MAX_SEC,
  COLLABORATION_DURATION_MIN_SEC,
  COLLABORATION_DURATION_MAX_SEC,
  getMeetingPointBySeat,
  DISCUSSION_BUBBLE_INTERVAL_SEC,
  DISCUSSION_BUBBLE_DURATION_SEC,
  USER_AVATAR_PALETTE,
  USER_AVATAR_DESK_COL,
  USER_AVATAR_DESK_ROW,
  TASK_DESKS,
  getSleepPoints,
  isInCeoOfficeZone,
  type TaskType,
} from "../constants.js";
import { getAssetInfo } from "../furniture/loader.js";
import { getDefaultLayout, layoutToTileMap } from "../layout.js";
import {
  CharacterState,
  Direction,
  TILE_SIZE,
  TileType,
  generateMatrixSeeds,
  isReadingTool,
} from "../types/index.js";

// Cache the walkability map
let cachedWalkMap: boolean[][] | null = null;

// Track occupied sleep spots to prevent stacking (key: "col,row")
const occupiedSleepSpots = new Set<string>();

/** Update occupied sleep spots from all characters (call before frame update) */
export function updateOccupiedSleepSpots(characters: Character[]): void {
  occupiedSleepSpots.clear();
  for (let i = 0; i < characters.length; i++) {
    const ch = characters[i];
    if (ch.state === CharacterState.SLEEP) {
      occupiedSleepSpots.add(`${Math.round(ch.tileCol)},${Math.round(ch.tileRow)}`);
    }
  }
}

/** Check if a position is occupied by a sleeping character */
function isSleepSpotOccupied(col: number, row: number): boolean {
  return occupiedSleepSpots.has(`${Math.round(col)},${Math.round(row)}`);
}

function getWalkMap(): boolean[][] {
  if (cachedWalkMap) return cachedWalkMap;

  const layout = getDefaultLayout();
  const tileMap = layoutToTileMap(layout);

  // Start with basic tile walkability (not VOID, not WALL)
  cachedWalkMap = tileMap.map((row) =>
    row.map((tile) => tile !== TileType.VOID && tile !== TileType.WALL),
  );

  // Mark furniture footprints as blocked
  const furniture = (layout as LayoutData & { furniture?: any[] }).furniture || [];
  for (const f of furniture) {
    const asset = getAssetInfo(f.type);
    if (!asset) continue;

    const bgTiles = asset.backgroundTiles || 0;

    // Block each tile in the furniture's footprint (except background tiles)
    for (let dr = bgTiles; dr < asset.footprintH; dr++) {
      for (let dc = 0; dc < asset.footprintW; dc++) {
        const row = f.row + dr;
        const col = f.col + dc;
        if (row >= 0 && row < cachedWalkMap.length && col >= 0 && col < cachedWalkMap[0].length) {
          cachedWalkMap[row][col] = false;
        }
      }
    }
  }

  return cachedWalkMap;
}

/** Clear the cached walk map (call when layout changes) */
export function clearWalkMapCache(): void {
  cachedWalkMap = null;
}

function isWalkable(col: number, row: number): boolean {
  const walkMap = getWalkMap();
  const r = Math.round(row);
  const c = Math.round(col);
  if (r < 0 || r >= walkMap.length) return false;
  if (c < 0 || c >= walkMap[0].length) return false;
  return walkMap[r][c];
}

function tileCenter(col: number, row: number): { x: number; y: number } {
  return {
    x: col * TILE_SIZE + TILE_SIZE / 2,
    y: row * TILE_SIZE + TILE_SIZE / 2,
  };
}

function directionBetween(
  fromCol: number,
  fromRow: number,
  toCol: number,
  toRow: number,
): Direction {
  const dc = toCol - fromCol;
  const dr = toRow - fromRow;
  if (dc > 0) return Direction.RIGHT;
  if (dc < 0) return Direction.LEFT;
  if (dr > 0) return Direction.DOWN;
  return Direction.UP;
}

// A* pathfinding that respects walls
function findPathToPoint(
  fromCol: number,
  fromRow: number,
  toCol: number,
  toRow: number,
): Array<{ col: number; row: number }> {
  const startCol = Math.round(fromCol);
  const startRow = Math.round(fromRow);
  const endCol = Math.round(toCol);
  const endRow = Math.round(toRow);

  // If destination is not walkable, find nearest walkable tile
  if (!isWalkable(endCol, endRow)) {
    // Try adjacent tiles
    const adjacent = [
      { col: endCol, row: endRow - 1 },
      { col: endCol, row: endRow + 1 },
      { col: endCol - 1, row: endRow },
      { col: endCol + 1, row: endRow },
    ];
    const walkableAdjacent = adjacent.find((p) => isWalkable(p.col, p.row));
    if (!walkableAdjacent) return [];
    return findPathToPoint(fromCol, fromRow, walkableAdjacent.col, walkableAdjacent.row);
  }

  // A* implementation
  interface Node {
    col: number;
    row: number;
    g: number;
    h: number;
    f: number;
    parent: Node | null;
  }

  const heuristic = (c: number, r: number) => Math.abs(c - endCol) + Math.abs(r - endRow);

  const openSet: Node[] = [
    {
      col: startCol,
      row: startRow,
      g: 0,
      h: heuristic(startCol, startRow),
      f: heuristic(startCol, startRow),
      parent: null,
    },
  ];

  const closedSet = new Set<string>();
  const key = (c: number, r: number) => `${c},${r}`;

  const MAX_ITERATIONS = 500;

  for (let i = 0; i < MAX_ITERATIONS && openSet.length > 0; i++) {
    // Find node with lowest f score
    openSet.sort((a, b) => a.f - b.f);
    const current = openSet.shift()!;

    if (current.col === endCol && current.row === endRow) {
      // Reconstruct path
      const path: Array<{ col: number; row: number }> = [];
      let node: Node | null = current;
      while (node && node.parent) {
        path.unshift({ col: node.col, row: node.row });
        node = node.parent;
      }
      return path;
    }

    closedSet.add(key(current.col, current.row));

    // Check neighbors (4-directional)
    const neighbors = [
      { col: current.col, row: current.row - 1 },
      { col: current.col, row: current.row + 1 },
      { col: current.col - 1, row: current.row },
      { col: current.col + 1, row: current.row },
    ];

    for (const n of neighbors) {
      if (closedSet.has(key(n.col, n.row))) continue;
      if (!isWalkable(n.col, n.row)) continue;

      const g = current.g + 1;
      const existing = openSet.find((o) => o.col === n.col && o.row === n.row);

      if (!existing) {
        openSet.push({
          col: n.col,
          row: n.row,
          g,
          h: heuristic(n.col, n.row),
          f: g + heuristic(n.col, n.row),
          parent: current,
        });
      } else if (g < existing.g) {
        existing.g = g;
        existing.f = g + existing.h;
        existing.parent = current;
      }
    }
  }

  // No path found - return empty
  return [];
}

// Sleep duration constants - agents sleep until needed (module-level for FSM access)
export const SLEEP_MIN_SEC = 60.0; // Minimum sleep 60 seconds
export const SLEEP_MAX_SEC = 300.0; // Maximum sleep 5 minutes

// Get the duration for an activity based on its type
function getActivityDuration(activity: string): number {
  switch (activity) {
    case "couch":
      return IDLE_COUCH_MIN_SEC + Math.random() * (IDLE_COUCH_MAX_SEC - IDLE_COUCH_MIN_SEC);
    case "coffee":
    case "water":
      return IDLE_COFFEE_MIN_SEC + Math.random() * (IDLE_COFFEE_MAX_SEC - IDLE_COFFEE_MIN_SEC);
    case "meeting":
      return (
        MEETING_DURATION_MIN_SEC +
        Math.random() * (MEETING_DURATION_MAX_SEC - MEETING_DURATION_MIN_SEC)
      );
    case "collaborate":
      return (
        COLLABORATION_DURATION_MIN_SEC +
        Math.random() * (COLLABORATION_DURATION_MAX_SEC - COLLABORATION_DURATION_MIN_SEC)
      );
    case "sleep":
      return SLEEP_MIN_SEC + Math.random() * (SLEEP_MAX_SEC - SLEEP_MIN_SEC);
    case "stand":
    case "window":
    default:
      return IDLE_STAND_MIN_SEC + Math.random() * (IDLE_STAND_MAX_SEC - IDLE_STAND_MIN_SEC);
  }
}

// Pick a random idle behavior point and start walking there
// Band members prefer to sleep when idle (they're not active workers)
function startRandomIdleBehavior(ch: Character, preferSleep = false): void {
  const pointNames = Object.keys(IDLE_POINTS);

  // Filter out current location and unreachable points
  let validPoints = pointNames.filter((name) => {
    const point = IDLE_POINTS[name];
    // Don't go to the same spot
    if (Math.round(ch.tileCol) === point.col && Math.round(ch.tileRow) === point.row) {
      return false;
    }
    // Band members cannot wander into the CEO office zone
    // Only the user avatar (CEO) is allowed there
    if (ch.isBandMember && isInCeoOfficeZone(point.col, point.row)) {
      return false;
    }
    // Check if there's a path
    const testPath = findPathToPoint(ch.tileCol, ch.tileRow, point.col, point.row);
    return testPath.length > 0;
  });

  if (validPoints.length === 0) {
    // No valid points - just stay idle
    ch.idleBehaviorTimer = 0;
    ch.idleDuration = IDLE_STAND_MIN_SEC;
    return;
  }

  // Band members prefer sleep spots (80% chance to go sleep if preferSleep is true)
  if (preferSleep && Math.random() < 0.8) {
    const sleepPoints = validPoints.filter((name) => {
      const point = IDLE_POINTS[name];
      // Only consider unoccupied sleep spots
      return point.activity === "sleep" && !isSleepSpotOccupied(point.col, point.row);
    });
    if (sleepPoints.length > 0) {
      validPoints = sleepPoints;
    }
  }

  // For any sleep point selection, filter out occupied spots
  validPoints = validPoints.filter((name) => {
    const point = IDLE_POINTS[name];
    if (point.activity === "sleep") {
      return !isSleepSpotOccupied(point.col, point.row);
    }
    return true;
  });

  const randomPointName = validPoints[Math.floor(Math.random() * validPoints.length)];
  const point = IDLE_POINTS[randomPointName];

  ch.idleTargetPoint = randomPointName;
  ch.path = findPathToPoint(ch.tileCol, ch.tileRow, point.col, point.row);

  if (ch.path.length === 0) {
    // Path failed somehow - stay idle
    ch.idleBehaviorTimer = 0;
    ch.idleDuration = IDLE_STAND_MIN_SEC;
    return;
  }

  ch.state = CharacterState.WANDER;
  ch.frame = 0;
  ch.frameTimer = 0;
  ch.idleBehaviorTimer = 0;
  ch.idleDuration = 0; // Will be set when arriving
}

export function createCharacter(
  id: string,
  name: string,
  palette: number,
  col: number,
  row: number,
  facingDir: Direction = Direction.DOWN,
  withSpawnEffect = true,
): Character {
  const center = tileCenter(col, row);
  return {
    id,
    name,
    state: CharacterState.IDLE, // Start idle - will wander until given work
    dir: facingDir,
    x: center.x,
    y: center.y,
    tileCol: col,
    tileRow: row,
    path: [],
    moveProgress: 0,
    currentTool: null,
    palette,
    frame: 0,
    frameTimer: 0,
    isActive: false, // Start not active - will wander around
    isSubagent: false,
    parentAgentId: null,
    bubbleType: null,
    bubbleTimer: 0,
    matrixEffect: withSpawnEffect ? "spawn" : null,
    matrixEffectTimer: 0,
    matrixEffectSeeds: withSpawnEffect ? generateMatrixSeeds() : [],
    selected: false,
    hovered: false,
    idleBehaviorTimer: 0,
    idleTargetPoint: null,
    homeDesk: { col, row }, // Remember starting desk position
  };
}

export function updateCharacter(ch: Character, dt: number): boolean {
  // Handle matrix effect animation
  if (ch.matrixEffect) {
    ch.matrixEffectTimer += dt;
    if (ch.matrixEffectTimer >= MATRIX_EFFECT_DURATION_SEC) {
      if (ch.matrixEffect === "spawn") {
        ch.matrixEffect = null;
        ch.matrixEffectTimer = 0;
        ch.matrixEffectSeeds = [];
      } else {
        // Despawn complete - return true to signal deletion
        return true;
      }
    }
    return false; // skip normal FSM while effect is active
  }

  // Tick bubble timer (for auto-clearing bubbles)
  if (ch.bubbleType === "waiting" || ch.bubbleType === "message") {
    ch.bubbleTimer -= dt;
    if (ch.bubbleTimer <= 0) {
      ch.bubbleType = null;
      ch.bubbleTimer = 0;
    }
  }

  ch.frameTimer += dt;

  switch (ch.state) {
    case CharacterState.TYPE: {
      if (ch.frameTimer >= TYPE_FRAME_DURATION_SEC) {
        ch.frameTimer -= TYPE_FRAME_DURATION_SEC;
        ch.frame = (ch.frame + 1) % 2;
      }
      if (!ch.isActive) {
        ch.state = CharacterState.IDLE;
        ch.frame = 0;
        ch.frameTimer = 0;
      }
      break;
    }

    case CharacterState.IDLE: {
      ch.frame = 0;
      if (ch.isActive) {
        // Return to desk if we were idle somewhere else
        const atHome =
          ch.homeDesk &&
          Math.round(ch.tileCol) === Math.round(ch.homeDesk.col) &&
          Math.round(ch.tileRow) === Math.round(ch.homeDesk.row);
        if (ch.homeDesk && !atHome) {
          ch.path = findPathToPoint(ch.tileCol, ch.tileRow, ch.homeDesk.col, ch.homeDesk.row);
          if (ch.path.length > 0) {
            ch.state = CharacterState.WALK;
            ch.idleTargetPoint = null;
          } else {
            // Can't reach desk, just start working here
            ch.state = CharacterState.TYPE;
          }
        } else {
          ch.state = CharacterState.TYPE;
        }
        ch.frame = 0;
        ch.frameTimer = 0;
      } else {
        // When not active, pick a random idle behavior after a delay
        ch.idleBehaviorTimer = (ch.idleBehaviorTimer || 0) + dt;
        if (ch.idleBehaviorTimer > IDLE_BEFORE_WANDER_SEC) {
          // Main agent (non-band-member, non-subagent) stays at desk 95% of time
          // Band members stay at their desks 90% of the time
          // This keeps the office looking professional with people at their desks
          const isMainAgent = !ch.isBandMember && !ch.isSubagent;
          const stayChance = isMainAgent ? 0.95 : 0.9;

          if (Math.random() < stayChance) {
            // Stay at desk - just reset timer and continue idle
            ch.idleBehaviorTimer = 0;
          } else {
            // Occasional wander to break room (couches, not sleep)
            startRandomIdleBehavior(ch, false);
          }
        }
      }
      break;
    }

    case CharacterState.SLEEP: {
      // Sleeping - stay still until woken up (by setting isActive)
      ch.frame = 0;
      ch.idleBehaviorTimer = (ch.idleBehaviorTimer || 0) + dt;

      // If activated, wake up and return to work
      if (ch.isActive) {
        ch.bubbleType = null; // Clear sleep bubble
        if (ch.homeDesk) {
          ch.path = findPathToPoint(ch.tileCol, ch.tileRow, ch.homeDesk.col, ch.homeDesk.row);
          if (ch.path.length > 0) {
            ch.state = CharacterState.WALK;
            ch.idleTargetPoint = null;
          } else {
            ch.state = CharacterState.TYPE;
          }
        } else {
          ch.state = CharacterState.TYPE;
        }
        ch.frame = 0;
        ch.frameTimer = 0;
        break;
      }

      // Natural wake up after sleep duration (stretch and pick new activity)
      if (ch.idleBehaviorTimer >= (ch.idleDuration || SLEEP_MIN_SEC)) {
        startRandomIdleBehavior(ch, ch.isBandMember ?? false);
      }
      break;
    }

    case CharacterState.SIT_COUCH:
    case CharacterState.DRINK: {
      // Chill at activity spots - duration set once on arrival, not every frame
      ch.frame = 0;
      ch.idleBehaviorTimer = (ch.idleBehaviorTimer || 0) + dt;

      // Check against the pre-set duration (not recalculated each frame!)
      if (ch.idleBehaviorTimer >= (ch.idleDuration || IDLE_STAND_MIN_SEC)) {
        // Done with this activity
        if (ch.isActive && ch.homeDesk) {
          // Return to desk if we have work
          ch.path = findPathToPoint(ch.tileCol, ch.tileRow, ch.homeDesk.col, ch.homeDesk.row);
          if (ch.path.length > 0) {
            ch.state = CharacterState.WALK;
            ch.idleTargetPoint = null;
          } else {
            ch.state = CharacterState.IDLE;
          }
        } else {
          // Pick a new activity
          startRandomIdleBehavior(ch);
        }
      }
      break;
    }

    case CharacterState.WANDER: {
      // Walking around randomly - same as WALK but picks new destination when done
      if (ch.frameTimer >= WALK_FRAME_DURATION_SEC) {
        ch.frameTimer -= WALK_FRAME_DURATION_SEC;
        ch.frame = (ch.frame + 1) % 4;
      }

      if (ch.path.length === 0) {
        const center = tileCenter(ch.tileCol, ch.tileRow);
        ch.x = center.x;
        ch.y = center.y;

        // Reached destination - switch to appropriate idle state based on activity type
        const targetPoint = ch.idleTargetPoint ? IDLE_POINTS[ch.idleTargetPoint] : null;

        if (targetPoint) {
          // Set facing direction from the idle point
          ch.dir = targetPoint.facing as Direction;

          // Set duration ONCE based on activity type (not recalculated each frame!)
          ch.idleDuration = getActivityDuration(targetPoint.activity);
          ch.idleBehaviorTimer = 0;

          // Set state based on activity
          switch (targetPoint.activity) {
            case "couch":
              ch.state = CharacterState.SIT_COUCH;
              break;
            case "coffee":
            case "water":
              ch.state = CharacterState.DRINK;
              break;
            case "meeting":
              ch.state = CharacterState.MEETING;
              ch.meetingTimer = 0;
              break;
            case "sleep":
              ch.state = CharacterState.SLEEP;
              ch.bubbleType = "sleep"; // Show Zzz bubble
              ch.bubbleTimer = 0;
              break;
            default:
              // stand, window, etc - just go to idle
              ch.state = CharacterState.IDLE;
              ch.idleBehaviorTimer = 0; // Reset timer for next wander
              break;
          }
        } else {
          ch.state = CharacterState.IDLE;
          ch.idleBehaviorTimer = 0;
        }

        ch.frame = 0;
        ch.frameTimer = 0;
        break;
      }

      const nextTile = ch.path[0];
      ch.dir = directionBetween(ch.tileCol, ch.tileRow, nextTile.col, nextTile.row);
      ch.moveProgress += (WALK_SPEED_PX_PER_SEC / TILE_SIZE) * dt;

      const fromCenter = tileCenter(ch.tileCol, ch.tileRow);
      const toCenter = tileCenter(nextTile.col, nextTile.row);
      const t = Math.min(ch.moveProgress, 1);
      ch.x = fromCenter.x + (toCenter.x - fromCenter.x) * t;
      ch.y = fromCenter.y + (toCenter.y - fromCenter.y) * t;

      if (ch.moveProgress >= 1) {
        ch.tileCol = nextTile.col;
        ch.tileRow = nextTile.row;
        ch.x = toCenter.x;
        ch.y = toCenter.y;
        ch.path.shift();
        ch.moveProgress = 0;
      }
      break;
    }

    case CharacterState.MEETING: {
      // In a meeting with other agents - sit at meeting table
      ch.frame = 0;
      ch.meetingTimer = (ch.meetingTimer || 0) + dt;

      // Check against the pre-set duration
      if (ch.meetingTimer >= (ch.idleDuration || MEETING_DURATION_MIN_SEC)) {
        // Meeting over - clear meeting state
        ch.meetingSeatIndex = null;
        ch.isMeetingInitiator = false;
        ch.meetingParticipants = [];

        if (ch.isActive && ch.homeDesk) {
          // Return to desk if we have work
          ch.path = findPathToPoint(ch.tileCol, ch.tileRow, ch.homeDesk.col, ch.homeDesk.row);
          if (ch.path.length > 0) {
            ch.state = CharacterState.WALK;
            ch.idleTargetPoint = null;
          } else {
            ch.state = CharacterState.IDLE;
          }
        } else {
          // Pick a new activity
          startRandomIdleBehavior(ch);
        }
      }
      break;
    }

    case CharacterState.COLLABORATE: {
      // Collaborating/pairing with another agent
      ch.frame = 0;
      ch.idleBehaviorTimer = (ch.idleBehaviorTimer || 0) + dt;

      // Check against the pre-set duration
      if (ch.idleBehaviorTimer >= (ch.idleDuration || COLLABORATION_DURATION_MIN_SEC)) {
        // Collaboration done - clear state
        ch.collaboratingWith = null;

        if (ch.isActive && ch.homeDesk) {
          // Return to desk if we have work
          ch.path = findPathToPoint(ch.tileCol, ch.tileRow, ch.homeDesk.col, ch.homeDesk.row);
          if (ch.path.length > 0) {
            ch.state = CharacterState.WALK;
            ch.idleTargetPoint = null;
          } else {
            ch.state = CharacterState.IDLE;
          }
        } else {
          // Pick a new activity
          startRandomIdleBehavior(ch);
        }
      }
      break;
    }

    case CharacterState.WALK: {
      if (ch.frameTimer >= WALK_FRAME_DURATION_SEC) {
        ch.frameTimer -= WALK_FRAME_DURATION_SEC;
        ch.frame = (ch.frame + 1) % 4;
      }

      if (ch.path.length === 0) {
        const center = tileCenter(ch.tileCol, ch.tileRow);
        ch.x = center.x;
        ch.y = center.y;
        ch.state = ch.isActive ? CharacterState.TYPE : CharacterState.IDLE;
        ch.frame = 0;
        ch.frameTimer = 0;
        break;
      }

      const nextTile = ch.path[0];
      ch.dir = directionBetween(ch.tileCol, ch.tileRow, nextTile.col, nextTile.row);
      ch.moveProgress += (WALK_SPEED_PX_PER_SEC / TILE_SIZE) * dt;

      const fromCenter = tileCenter(ch.tileCol, ch.tileRow);
      const toCenter = tileCenter(nextTile.col, nextTile.row);
      const t = Math.min(ch.moveProgress, 1);
      ch.x = fromCenter.x + (toCenter.x - fromCenter.x) * t;
      ch.y = fromCenter.y + (toCenter.y - fromCenter.y) * t;

      if (ch.moveProgress >= 1) {
        ch.tileCol = nextTile.col;
        ch.tileRow = nextTile.row;
        ch.x = toCenter.x;
        ch.y = toCenter.y;
        ch.path.shift();
        ch.moveProgress = 0;
      }
      break;
    }
  }

  return false;
}

export function getCharacterSprite(ch: Character, sprites: CharacterSprites): SpriteData {
  switch (ch.state) {
    case CharacterState.TYPE:
      // Use reading animation for reading tools, typing for everything else
      if (isReadingTool(ch.currentTool)) {
        return sprites.reading[ch.dir][ch.frame % 2];
      }
      return sprites.typing[ch.dir][ch.frame % 2];
    case CharacterState.WALK:
    case CharacterState.WANDER:
      return sprites.walk[ch.dir][ch.frame % 4];
    case CharacterState.IDLE:
      return sprites.walk[ch.dir][1];
    case CharacterState.SLEEP:
      // Sleep uses idle frame with down direction
      return sprites.walk[Direction.DOWN][1];
    case CharacterState.SIT_COUCH:
      // Sitting on couch - use idle frame in the direction set by the idle point
      return sprites.walk[ch.dir][1];
    case CharacterState.DRINK:
      // At water cooler/coffee - use idle frame in the direction set by the idle point
      return sprites.walk[ch.dir][1];
    case CharacterState.MEETING:
      // In meeting - use idle frame facing the table
      return sprites.walk[ch.dir][1];
    case CharacterState.COLLABORATE:
      // Collaborating - use idle frame facing partner
      return sprites.walk[ch.dir][1];
    default:
      return sprites.walk[ch.dir][1];
  }
}

export function setCharacterSleeping(ch: Character, sleeping: boolean): void {
  if (sleeping) {
    ch.state = CharacterState.SLEEP;
    ch.isActive = false;
    ch.bubbleType = null;
  } else {
    ch.state = CharacterState.IDLE;
  }
}

export function setCharacterActive(ch: Character, active: boolean, tool?: string): void {
  const wasActive = ch.isActive;
  ch.isActive = active;
  ch.currentTool = tool ?? null;

  // Show message bubble when becoming active (new task incoming)
  if (active && !wasActive) {
    showMessageBubble(ch);
  }
}

export function showPermissionBubble(ch: Character): void {
  ch.bubbleType = "permission";
  ch.bubbleTimer = 0;
}

export function showWaitingBubble(ch: Character): void {
  ch.bubbleType = "waiting";
  ch.bubbleTimer = WAITING_BUBBLE_DURATION_SEC;
}

export function showMessageBubble(ch: Character): void {
  ch.bubbleType = "message";
  ch.bubbleTimer = MESSAGE_BUBBLE_DURATION_SEC;
}

export function clearBubble(ch: Character): void {
  ch.bubbleType = null;
  ch.bubbleTimer = 0;
  ch.attentionCount = undefined;
}

/** Show warning bubble with count of items needing attention */
export function showWarningBubble(ch: Character, attentionCount: number): void {
  ch.bubbleType = "warning";
  ch.bubbleTimer = 0; // Warning stays until cleared
  ch.attentionCount = attentionCount;
}

/** Show setup bubble for onboarding items */
export function showSetupBubble(ch: Character): void {
  ch.bubbleType = "setup";
  ch.bubbleTimer = 0; // Setup stays until cleared
}

/** Show success bubble (green checkmark) for completed tasks */
export function showSuccessBubble(ch: Character, durationSec = 2.0): void {
  ch.bubbleType = "success";
  ch.bubbleTimer = durationSec;
}

export function startDespawn(ch: Character): void {
  ch.matrixEffect = "despawn";
  ch.matrixEffectTimer = 0;
  ch.matrixEffectSeeds = generateMatrixSeeds();
  ch.bubbleType = null;
}

/**
 * Start a meeting for a character - sends them to the meeting room
 * @param ch The character to send to the meeting
 * @param seatIndex The meeting room seat to take (0-5)
 * @param participantIds IDs of other agents in the meeting
 * @param isInitiator Whether this character initiated the meeting
 */
export function startMeeting(
  ch: Character,
  seatIndex: number,
  participantIds: string[],
  isInitiator: boolean,
): boolean {
  const meetingPoint = getMeetingPointBySeat(seatIndex);
  if (!meetingPoint) return false;

  const path = findPathToPoint(ch.tileCol, ch.tileRow, meetingPoint.col, meetingPoint.row);
  if (path.length === 0 && (ch.tileCol !== meetingPoint.col || ch.tileRow !== meetingPoint.row)) {
    return false; // Can't reach meeting room
  }

  ch.meetingSeatIndex = seatIndex;
  ch.isMeetingInitiator = isInitiator;
  ch.meetingParticipants = participantIds;
  ch.idleTargetPoint = `meetingSeat${seatIndex}`;
  ch.idleDuration = getActivityDuration("meeting");
  ch.meetingTimer = 0;

  if (path.length > 0) {
    ch.path = path;
    ch.state = CharacterState.WANDER;
    ch.frame = 0;
    ch.frameTimer = 0;
  } else {
    // Already at seat
    ch.state = CharacterState.MEETING;
    ch.dir = meetingPoint.facing as Direction;
    ch.frame = 0;
    ch.frameTimer = 0;
  }

  return true;
}

/**
 * Start collaboration between two characters
 * The initiator will walk to the target's location
 */
export function startCollaboration(
  initiator: Character,
  targetId: string,
  targetCol: number,
  targetRow: number,
): boolean {
  // Walk to a spot near the target
  const adjacentSpots = [
    { col: targetCol - 1, row: targetRow },
    { col: targetCol + 1, row: targetRow },
    { col: targetCol, row: targetRow - 1 },
    { col: targetCol, row: targetRow + 1 },
  ];

  for (const spot of adjacentSpots) {
    const path = findPathToPoint(initiator.tileCol, initiator.tileRow, spot.col, spot.row);
    if (path.length > 0) {
      initiator.path = path;
      initiator.state = CharacterState.WALK;
      initiator.collaboratingWith = targetId;
      initiator.idleDuration = getActivityDuration("collaborate");
      initiator.idleBehaviorTimer = 0;
      initiator.frame = 0;
      initiator.frameTimer = 0;
      return true;
    }
  }

  return false;
}

/**
 * Set up a visual handoff animation between two characters
 */
export function startHandoffAnimation(sender: Character, receiverId: string): void {
  sender.handoffProgress = 0;
  sender.handoffTargetId = receiverId;
}

/**
 * Update handoff animation progress
 * @returns true if animation is complete
 */
export function updateHandoffAnimation(ch: Character, dt: number): boolean {
  if (ch.handoffProgress === undefined || ch.handoffProgress === null) return true;
  if (ch.handoffTargetId === null) return true;

  // Handoff animation takes 1.5 seconds
  const HANDOFF_DURATION = 1.5;
  ch.handoffProgress += dt / HANDOFF_DURATION;

  if (ch.handoffProgress >= 1) {
    ch.handoffProgress = undefined;
    ch.handoffTargetId = null;
    return true;
  }

  return false;
}

/**
 * Check if a character is available for a meeting (not busy, not already in meeting)
 */
export function isAvailableForMeeting(ch: Character): boolean {
  if (ch.isActive) return false; // Busy with work
  if (ch.state === CharacterState.MEETING) return false;
  if (ch.state === CharacterState.COLLABORATE) return false;
  if (ch.matrixEffect) return false; // Spawning/despawning
  return true;
}

/**
 * Check if a character is currently in the meeting room
 */
export function isInMeetingRoom(ch: Character): boolean {
  return ch.state === CharacterState.MEETING && ch.meetingSeatIndex !== null;
}

/**
 * Create the user avatar (CEO) character
 * Uses a special palette and is positioned at the CEO desk
 */
export function createUserAvatar(id: string, name: string): Character {
  const center = tileCenter(USER_AVATAR_DESK_COL, USER_AVATAR_DESK_ROW);
  return {
    id,
    name,
    state: CharacterState.IDLE,
    dir: Direction.DOWN, // Looking down at the office
    x: center.x,
    y: center.y,
    tileCol: USER_AVATAR_DESK_COL,
    tileRow: USER_AVATAR_DESK_ROW,
    path: [],
    moveProgress: 0,
    currentTool: null,
    palette: USER_AVATAR_PALETTE,
    frame: 0,
    frameTimer: 0,
    isActive: false,
    isSubagent: false,
    parentAgentId: null,
    bubbleType: null,
    bubbleTimer: 0,
    matrixEffect: "spawn",
    matrixEffectTimer: 0,
    matrixEffectSeeds: generateMatrixSeeds(),
    selected: false,
    hovered: false,
    isUserAvatar: true,
    homeDesk: { col: USER_AVATAR_DESK_COL, row: USER_AVATAR_DESK_ROW },
    focusTargetId: null,
  };
}

/**
 * Show speech bubble on user avatar (when user sends a message)
 */
export function showSpeechBubble(ch: Character, durationSec: number = MESSAGE_BUBBLE_DURATION_SEC): void {
  ch.bubbleType = "speech";
  ch.bubbleTimer = durationSec;
}

/**
 * Start a discussion animation between multiple characters
 * Characters will alternate showing discussion bubbles
 */
export function startDiscussion(participants: Character[]): void {
  for (let i = 0; i < participants.length; i++) {
    const ch = participants[i];
    ch.isDiscussing = true;
    ch.discussionTimer = i * (DISCUSSION_BUBBLE_INTERVAL_SEC / participants.length);
    // First participant shows bubble immediately
    if (i === 0) {
      ch.bubbleType = "discuss";
      ch.bubbleTimer = DISCUSSION_BUBBLE_DURATION_SEC;
    }
  }
}

/**
 * Update discussion animation for a character
 * Call this from updateCharacter for characters in discussion
 */
export function updateDiscussion(ch: Character, dt: number): void {
  if (!ch.isDiscussing) return;

  ch.discussionTimer = (ch.discussionTimer || 0) + dt;

  // Check if it's time to show bubble
  const cycleTime = ch.discussionTimer % DISCUSSION_BUBBLE_INTERVAL_SEC;
  if (cycleTime < dt && ch.bubbleType !== "discuss") {
    // Start of a new cycle for this character
    ch.bubbleType = "discuss";
    ch.bubbleTimer = DISCUSSION_BUBBLE_DURATION_SEC;
  }

  // Auto-hide bubble after duration
  if (ch.bubbleType === "discuss") {
    ch.bubbleTimer -= dt;
    if (ch.bubbleTimer <= 0) {
      ch.bubbleType = null;
      ch.bubbleTimer = 0;
    }
  }
}

/**
 * Stop discussion animation for a character
 */
export function stopDiscussion(ch: Character): void {
  ch.isDiscussing = false;
  ch.discussionTimer = 0;
  if (ch.bubbleType === "discuss") {
    ch.bubbleType = null;
    ch.bubbleTimer = 0;
  }
}

/**
 * Set characters as working together on the same task
 * Creates visual connections between them
 */
export function setWorkingTogether(characters: Character[]): void {
  const ids = characters.map((ch) => ch.id);
  for (const ch of characters) {
    ch.workingTogetherWith = ids.filter((id) => id !== ch.id);
  }
}

/**
 * Clear working together state for a character
 */
export function clearWorkingTogether(ch: Character): void {
  ch.workingTogetherWith = [];
}

/**
 * Set focus target for user avatar (visual line to focused agent)
 */
export function setUserFocus(userAvatar: Character, targetId: string | null): void {
  if (userAvatar.isUserAvatar) {
    userAvatar.focusTargetId = targetId;
  }
}

/**
 * Move user avatar to observe a specific agent's desk
 */
export function moveUserToAgent(userAvatar: Character, targetCol: number, targetRow: number): boolean {
  if (!userAvatar.isUserAvatar) return false;

  // Find a spot adjacent to the target
  const adjacentSpots = [
    { col: targetCol - 1, row: targetRow },
    { col: targetCol + 1, row: targetRow },
    { col: targetCol, row: targetRow - 1 },
    { col: targetCol, row: targetRow + 1 },
  ];

  for (const spot of adjacentSpots) {
    const path = findPathToPoint(userAvatar.tileCol, userAvatar.tileRow, spot.col, spot.row);
    if (path.length > 0) {
      userAvatar.path = path;
      userAvatar.state = CharacterState.WALK;
      userAvatar.frame = 0;
      userAvatar.frameTimer = 0;
      return true;
    }
  }

  return false;
}

/**
 * Return user avatar to CEO desk
 */
export function returnUserToDesk(userAvatar: Character): boolean {
  if (!userAvatar.isUserAvatar || !userAvatar.homeDesk) return false;

  const path = findPathToPoint(
    userAvatar.tileCol,
    userAvatar.tileRow,
    userAvatar.homeDesk.col,
    userAvatar.homeDesk.row,
  );

  if (path.length > 0) {
    userAvatar.path = path;
    userAvatar.state = CharacterState.WALK;
    userAvatar.frame = 0;
    userAvatar.frameTimer = 0;
    return true;
  }

  return false;
}

/**
 * Move agent to a task-specific desk location
 * Agent walks to the desk and starts typing animation
 */
export function moveAgentToTask(agent: Character, taskType: TaskType): boolean {
  // Don't move user avatars or subagents
  if (agent.isUserAvatar || agent.isSubagent) return false;

  const taskDesk = TASK_DESKS[taskType];
  if (!taskDesk) return false;

  // Find path to task desk
  const path = findPathToPoint(agent.tileCol, agent.tileRow, taskDesk.col, taskDesk.row);

  if (path.length > 0) {
    agent.path = path;
    agent.state = CharacterState.WALK;
    agent.dir = taskDesk.facing as Direction;
    agent.frame = 0;
    agent.frameTimer = 0;
    // Mark agent as active so they show typing animation when arriving
    agent.isActive = true;
    return true;
  }

  // Already at or near the desk, just start working
  if (
    Math.abs(agent.tileCol - taskDesk.col) <= 1 &&
    Math.abs(agent.tileRow - taskDesk.row) <= 1
  ) {
    agent.dir = taskDesk.facing as Direction;
    agent.state = CharacterState.TYPE;
    agent.isActive = true;
    return true;
  }

  return false;
}
